import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { Calendar, MapPin, Users, ExternalLink, Globe, Ticket, DollarSign, X } from 'lucide-react';
import { LoadingSpinner, Button, Modal, ShareTriggerButton } from '../components/ui';
import { PageHeader, ContentCard } from '../components/layout';
import { toast } from '../components/ui/Toaster';
import { eventService } from '../services/eventService';
import { ShareMenu } from '../components/sharing';
import { useAuthStore } from '../store/authStore';
import type { Event } from '../types';

const EventDetailsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showJustCreatedBanner, setShowJustCreatedBanner] = useState(
    Boolean((location.state as { justCreated?: boolean } | null)?.justCreated)
  );
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadEvent(slug);
    }
  }, [slug]);

  const loadEvent = async (eventSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to get event by slug first, fallback to ID if it's a number
      const eventData = await eventService.getEventBySlug(eventSlug);
      setEvent(eventData);
    } catch (error: any) {
      console.error('Failed to load event:', error);
      setError('Evenement niet gevonden of laden mislukt');
      toast.error('Fout', 'Laden van evenement details mislukt');
    } finally {
      setLoading(false);
    }
  };

  const closeCancelModal = () => {
    if (canceling) return;
    setShowCancelModal(false);
    setCancelError(null);
  };

  const handleCancelEvent = async () => {
    if (!event) return;

    try {
      setCanceling(true);
      setCancelError(null);

      await eventService.deleteEvent(event.id);

      toast.success('Geannuleerd', 'Je evenement is offline gehaald.');
      navigate('/dashboard');
    } catch (error: any) {
      const status = error.response?.status;
      const code = error.response?.data?.error;

      if (status === 409 || code === 'EVENT_HAS_GROUPS') {
        // Er hangen al groepen aan het evenement: alleen een beheerder kan
        // nog annuleren.
        setCancelError(
          'Er zijn al groepen aangemaakt voor dit evenement. Neem contact op met de beheerder om het te laten annuleren.'
        );
      } else {
        const message = error.response?.data?.message || error.message || 'Annuleren mislukt.';
        setCancelError(message);
        toast.error('Fout', message);
      }
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };


  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner size="lg" text="Evenement details laden..." />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-fg-bright mb-4">Evenement niet gevonden</h1>
          <p className="text-fg-muted mb-6">{error || 'Het evenement dat je zoekt bestaat niet.'}</p>
          <Link to="/events">
            <Button>Terug naar Evenementen</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isOwner = Boolean(user && event.creator && event.creator.id === user.id);
  const isPublished = event.status === 'PUBLISHED';

  return (
    <div className="space-y-6">
      {/* Net aangemaakt: succesbanner */}
      {showJustCreatedBanner && (
        <div className="sf-card-highlight relative overflow-hidden p-6 sm:p-7">
          <button
            onClick={() => setShowJustCreatedBanner(false)}
            className="absolute right-4 top-4 p-1.5 text-fg-muted hover:text-fg-bright transition-colors"
          >
            <span className="sr-only">Sluiten</span>
            <X className="h-4 w-4" />
          </button>
          <h2 className="text-lg font-bold text-fg-bright pr-8">
            Je evenement staat live — start nu je groep!
          </h2>
          <p className="mt-2 text-sm text-fg-body max-w-xl">
            Iedereen kan het evenement nu al vinden op Solofix. Ons team controleert het achteraf nog even mee.
          </p>
          <Link to={`/groups/create?eventId=${event.id}`} className="mt-4 inline-block">
            <Button size="md">Maak nu een groep</Button>
          </Link>
        </div>
      )}

      {/* Niet (meer) actief: status-banner voor alle bezoekers, met extra uitleg voor de maker */}
      {event.status === 'REJECTED' && (
        <div className="rounded-card border border-neon-pink/30 bg-neon-pink/[.07] p-6">
          <h2 className="text-lg font-bold text-fg-bright">
            {isOwner ? 'Evenement offline gehaald' : 'Dit evenement is afgekeurd en niet meer actief'}
          </h2>
          <p className="mt-2 text-sm text-fg-body">
            {isOwner
              ? `Je evenement is door het team offline gehaald${event.rejectionReason ? `: ${event.rejectionReason}` : '.'}`
              : 'Dit evenement is afgekeurd door het team en is niet meer zichtbaar of actief.'}
          </p>
        </div>
      )}

      {event.status === 'CANCELLED' && (
        <div className="rounded-card border border-neon-pink/30 bg-neon-pink/[.07] p-6">
          <h2 className="text-lg font-bold text-fg-bright">Dit evenement is geannuleerd</h2>
          <p className="mt-2 text-sm text-fg-body">
            {isOwner
              ? 'Je hebt dit evenement geannuleerd. Het is offline en niet meer zichtbaar voor anderen.'
              : 'Dit evenement is geannuleerd en niet meer actief.'}
          </p>
        </div>
      )}

      {/* Header */}
      <PageHeader
        title={event.title}
        subtitle={
          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-neon-cyan" />
              <span>{formatDateShort(event.startDate)}</span>
              {event.endDate && <span> - {formatDateShort(event.endDate)}</span>}
            </div>
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-neon-pink" />
              <span>{event.location}{event.venue && `, ${event.venue}`}</span>
            </div>
            {event.capacity && (
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-2 text-neon-cyan" />
                <span>Tot {event.capacity} deelnemers</span>
              </div>
            )}
          </div>
        }
        breadcrumb={
          <>
            <Link to="/events" className="sf-link text-sm">
              ← Terug naar Evenementen
            </Link>
            <div className="sf-eyebrow mt-4">{'// event'}</div>
          </>
        }
        actions={
          <>
            <ShareTriggerButton
              onClick={() => setShowShareMenu(true)}
              variant="outline"
              size="md"
              className="w-full sm:w-auto"
            />
            {isPublished && (
              <Link to={`/groups?eventId=${event.id}`} className="w-full sm:w-auto">
                <Button size="md" className="w-full sm:w-auto">Bekijk groepen</Button>
              </Link>
            )}
          </>
        }
      />

      {/* Single Content Card */}
      <ContentCard>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Event Details Section */}
            <div>
              <h2 className="text-xl font-bold text-fg-bright mb-6">Evenement details</h2>

              <div className="space-y-6">
                <div>
                  <h3 className="sf-label-mono mb-2">Datum</h3>
                  <p className="text-fg-body">{formatDate(event.startDate)}</p>
                  {event.endDate && (
                    <p className="text-fg-body">Eindigt: {formatDate(event.endDate)}</p>
                  )}
                </div>

                <div>
                  <h3 className="sf-label-mono mb-2">Locatie</h3>
                  <p className="text-fg-body">
                    {event.location}
                    {event.venue && (
                      <>
                        <br />
                        <span className="font-semibold text-fg-soft">{event.venue}</span>
                      </>
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="sf-label-mono mb-2">Evenement type</h3>
                  <span className="sf-badge-cyan">
                    {event.type}
                  </span>
                </div>

                {event.description && (
                  <div>
                    <h3 className="sf-label-mono mb-2">Beschrijving</h3>
                    <p className="text-fg-body whitespace-pre-wrap">{event.description}</p>
                  </div>
                )}
              </div>
            </div>
            
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event Info - only show if there's any info to display */}
            {(event.capacity || event.price || event.website || event.ticketUrl) && (
              <div className="sf-item p-4">
                <div className="space-y-3">
                  {event.capacity && (
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-neon-cyan mr-3" />
                      <span className="text-sm text-fg-body">Max {event.capacity} deelnemers</span>
                    </div>
                  )}

                  {event.price && (
                    <div className="flex items-center">
                      <DollarSign className="h-4 w-4 text-neon-lime mr-3" />
                      <span className="text-sm text-fg-body">{event.price}</span>
                    </div>
                  )}

                  {event.website && (
                    <a
                      href={event.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-neon-cyan transition-opacity hover:opacity-80"
                    >
                      <Globe className="h-4 w-4 mr-3" />
                      <span className="text-sm font-semibold">Officiële Website</span>
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  )}

                  {event.ticketUrl && (
                    <a
                      href={event.ticketUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-neon-cyan transition-opacity hover:opacity-80"
                    >
                      <Ticket className="h-4 w-4 mr-3" />
                      <span className="text-sm font-semibold">Koop tickets</span>
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Action Buttons: alleen tonen zolang het evenement live staat */}
            {isPublished && (
              <div className="sf-item p-4">
                <h3 className="font-bold text-fg-bright mb-4">Acties</h3>
                <div className="space-y-3">
                  <Link to={`/groups/create?eventId=${event.id}`} className="block">
                    <Button className="w-full" size="md">Maak een groep</Button>
                  </Link>

                  <Link to={`/groups?eventId=${event.id}`} className="block">
                    <Button variant="outline" className="w-full" size="md">Bekijk groepen</Button>
                  </Link>

                  {/* Maker kan zijn eigen evenement annuleren zolang het live staat */}
                  {isOwner && (
                    <button
                      type="button"
                      onClick={() => setShowCancelModal(true)}
                      className="w-full pt-2 text-sm font-semibold text-fg-muted transition-colors hover:text-neon-pink-soft"
                    >
                      Evenement annuleren
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </ContentCard>

      {/* Share Menu */}
      {event && (
        <ShareMenu
          type="event"
          data={event}
          isOpen={showShareMenu}
          onClose={() => setShowShareMenu(false)}
        />
      )}

      {/* Evenement annuleren: bevestigingsmodal */}
      <Modal isOpen={showCancelModal} onClose={closeCancelModal} title="Evenement annuleren" size="sm">
        <p className="text-sm text-fg-body">
          Weet je zeker dat je <strong className="text-fg-bright">{event.title}</strong> wilt
          annuleren? Het evenement gaat offline en is daarna niet meer zichtbaar voor anderen.
          Dit kun je niet zelf ongedaan maken.
        </p>

        {cancelError && (
          <div className="mt-4 rounded-lg border border-neon-pink/30 bg-neon-pink/[.07] p-3 text-sm text-fg-body">
            {cancelError}
          </div>
        )}

        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={closeCancelModal} disabled={canceling}>
            Terug
          </Button>
          <Button variant="danger" onClick={handleCancelEvent} disabled={canceling}>
            {canceling ? 'Annuleren...' : 'Evenement annuleren'}
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default EventDetailsPage;